import React, { useMemo, useRef, useState } from 'react';
import { FormGenericContainer } from '../FormGenericContainer';
import { BubbleAccordion, BubbleButton, BubbleInput } from '../BubbleUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canManageLibraries } from '@go-shared/lib/permissions';
import EventTemplateForm, {
  EventTemplateFormData,
  EventTemplateFormRef,
} from '../BasicForms/EventTemplateForm';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { DefaultEventDisplayOption, EventTemplate } from '@go-shared/types/eventTemplate';
import { forceIdToString, safeIdCompare } from '@go-shared/lib/utils';
import { bare, create, populate } from '@go-client/lib/api/eventTemplate';
import { User } from '@go-shared/types/user';
import { intersection } from '@go-shared/lib/functional';
import { BubbleAccordionRef } from '../BubbleUI/BubbleAccordion';
import { Brand } from '@go-shared/types/brand';
import { Property } from '@go-shared/types/property';
import { TextHighlighted } from '@go-client/components/Common/TextHighlighted';
import { Plan } from '@go-shared/types/plan';
import { CanvasEventFormMode } from './CanvasEventForm';
import { evaluateAudienceZone } from '@go-shared/lib/audience';

interface SelectEventPageProps {
  user: User;
  property: Property;
  brand: Brand;
  mode: CanvasEventFormMode;
  plan: Plan;
  title?: string;
  selectedEvtTemplate?: EventTemplate;
  onCreateEvtTemplate?: (tmpl: EventTemplate) => void;
  onSelectEvtTemplate?: (tmpl: EventTemplate | undefined) => void;
  eventTemplates: EventTemplate[];
  onGoNext?: () => void;
  onClose?: () => void;
}

export default function SelectEventPage(props: SelectEventPageProps) {
  const { user, plan, eventTemplates, selectedEvtTemplate } = props;

  const [formFilter, setFormFilter] = useState<EventTemplateFormData | undefined>(undefined);
  const [typeaheadFilterAll, setTypeaheadFilterAll] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(false);

  const evtTmplFormRef = useRef<EventTemplateFormRef>(null);
  const accordionRef = useRef<BubbleAccordionRef>(null);

  function handleSelectEvtTemplate(eventTmpl: EventTemplate | undefined) {
    if (props.onSelectEvtTemplate) props.onSelectEvtTemplate(eventTmpl);
  }

  async function handleCreateEventTemplate() {
    if (!formFilter) return;

    const bareTemplate = await create({
      ...formFilter,
      mcm: formFilter.mcm,
      audience: formFilter.audience,
      source: props.property._id,
      brand: props.brand._id,
      incomplete: true,
      properties: [{ property: props.property._id, defaultStaffAllocations: [] }],
      images: formFilter.images,
      softConflict: false,
      interactiveFeedDisplay: formFilter.interactiveFeedDisplay ?? DefaultEventDisplayOption,
      callToActions: formFilter.callToActions || [],
    });

    if (bareTemplate && props.onCreateEvtTemplate) {
      const template = populate(bareTemplate, props.brand);
      accordionRef.current?.toggle(1);
      props.onCreateEvtTemplate(template);
    }
  }

  function handleCopySelectedEvtTemplate() {
    evtTmplFormRef.current?.reset();
    accordionRef.current?.toggle(0);
    handleSelectEvtTemplate(undefined);
  }

  const evtTmplItems = useMemo(() => {
    return eventTemplates
      .map(e => ({
        id: e._id,
        title: e.title,
        description: e.description,
        _template: e,
      }))
      .filter(e => !e._template.archived);
  }, [eventTemplates]);

  const titleFuse = useMemo(() => {
    return new Fuse(evtTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['title'],
      useExtendedSearch: true,
    });
  }, [evtTmplItems]);

  const descFuse = useMemo(() => {
    return new Fuse(evtTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['description'],
    });
  }, [evtTmplItems]);

  const titleFuseSearchResults = useMemo(() => {
    if (!formFilter?.title || !titleFuse) return undefined;
    const criteria = `'${formFilter.title}`;

    return titleFuse.search(criteria).map(s => s.item);
  }, [titleFuse, formFilter]);

  const descFuseSearchResults = useMemo(() => {
    if (!formFilter?.description || !descFuse) return undefined;
    return descFuse.search(formFilter.description).map(s => s.item);
  }, [descFuse, formFilter]);

  let filteredItems = evtTmplItems;

  if (titleFuseSearchResults) {
    filteredItems = intersection(filteredItems, titleFuseSearchResults, 'id');
  }

  if (descFuseSearchResults) {
    filteredItems = intersection(filteredItems, descFuseSearchResults, 'id');
  }

  if (formFilter) {
    filteredItems = filteredItems.filter(wi => {
      let meetsSegmentCheck = true;
      let meetsLevelCheck = true;
      let meetsAttributesCheck = true;
      let meetsAudienceCheck = true;

      if (formFilter.segment) {
        meetsSegmentCheck = safeIdCompare(wi._template.segment?._id, formFilter.segment);
      }

      if (formFilter.levels.length) {
        const len = intersection(
          [...wi._template.levels].map(l => forceIdToString(l._id)),
          formFilter.levels
        ).length;
        meetsLevelCheck = typeaheadFilterAll ? len === formFilter.levels.length : len > 0;
      }

      if (formFilter.attributes.length) {
        const len = intersection(
          [...wi._template.attributes].map(a => forceIdToString(a._id)),
          formFilter.attributes
        ).length;
        meetsAttributesCheck = typeaheadFilterAll ? len === formFilter.attributes.length : len > 0;
      }

      if (formFilter.audienceZoneFilter) {
        meetsAudienceCheck = evaluateAudienceZone(
          'visible',
          formFilter.audienceZoneFilter,
          wi._template.audience,
          undefined
        );
      }

      return meetsSegmentCheck && meetsLevelCheck && meetsAttributesCheck && meetsAudienceCheck;
    });
  }

  return (
    <div className="cef-1">
      <FormGenericContainer
        className="absolute top-0 left-0 w-full h-full flex flex-col"
        bodyClassName="p-0-i flex-grow relative bg-eggshell"
        footerClassName="p-4-i border-t"
        footer={() => (
          <>
            {selectedEvtTemplate && (
              <div className="text-slate-dark">
                <div>
                  Event Selected:
                  <span className="font-semibold"> {selectedEvtTemplate.title}</span>
                </div>
              </div>
            )}
            <div className="ml-auto" />
            <BubbleButton
              variant="slate"
              inverse
              onClick={() => setTypeaheadFilterAll(!typeaheadFilterAll)}
              icon="filter"
            >
              Typeahead Filter Mode: {typeaheadFilterAll ? 'All' : 'Any'}
            </BubbleButton>
            <BubbleButton
              variant="navy"
              disabled={!props.selectedEvtTemplate}
              onClick={props.onGoNext}
            >
              Next: Enter Event Details
            </BubbleButton>
          </>
        )}
      >
        <div
          className={`absolute left-0 top-0 h-full p-6 ${
            selectedEvtTemplate ? 'overflow-clip' : 'overflow-y-auto overflow-x-hidden'
          }`}
          style={{ width: 'calc(100% - 400px)' }}
        >
          <div className="flex items-start mb-8">
            <div>
              <h1 className="text-lg text-slate-dark font-semibold">
                {props.title || 'Add Event'}
              </h1>
              <h2 className="text-2xl text-navy">1. Select Event</h2>
            </div>
            <BubbleButton
              onClick={props.onClose}
              className="ml-auto"
              variant="navy"
              inverse
              icon="xmark-large"
              iconSize="xl"
            />
          </div>
          <EventTemplateForm
            ref={evtTmplFormRef}
            edit={selectedEvtTemplate ? bare(selectedEvtTemplate) : undefined}
            brand={plan.property.brand}
            property={plan.property}
            onFormUpdated={setFormFilter}
            mode={isNewTemplate ? 'edit' : 'filter'}
          />
          <div
            className="absolute top-0 left-0 w-full h-full bg-black opacity-25 cursor-pointer transition-transform"
            onClick={() => handleSelectEvtTemplate(undefined)}
            style={{
              transform: `translateX(${selectedEvtTemplate ? '0' : '100%'})`,
            }}
          />
          <div
            className="absolute left-0 bottom-0 z-10 w-full p-6 overflow-y-auto bg-eggshell transition-transform"
            style={{
              height: '90%',
              transform: `translateX(${selectedEvtTemplate ? '0' : '100%'})`,
              pointerEvents: selectedEvtTemplate ? 'auto' : 'none',
            }}
          >
            <div className="flex items-start mb-8">
              <div>
                <h1 className="text-lg text-slate-dark font-semibold">
                  {selectedEvtTemplate?.title ?? 'Unknown Template'}
                </h1>
                <h2 className="text-2xl text-navy">Event Details</h2>
              </div>
              <div className="ml-auto" />
              {canManageLibraries(user) && (
                <BubbleButton
                  variant="banana"
                  icon="copy"
                  inverse
                  onClick={handleCopySelectedEvtTemplate}
                  tabIndex={-1}
                >
                  Duplicate This Event
                </BubbleButton>
              )}
              <BubbleButton
                onClick={() => handleSelectEvtTemplate(undefined)}
                variant="navy"
                inverse
                icon="xmark-large"
                iconSize="xl"
                tabIndex={-1}
              />
            </div>
            {selectedEvtTemplate && (
              <EventTemplateForm
                edit={bare(selectedEvtTemplate)}
                brand={plan.property.brand}
                property={plan.property}
                mode="readonly"
              />
            )}
          </div>
        </div>

        <div className="absolute right-0 top-0 h-full bg-white border-l" style={{ width: '400px' }}>
          <BubbleAccordion
            ref={accordionRef}
            variant="navy-light"
            singleMode
            noToggle
            headerClassName="px-4 py-4"
            className="h-full flex flex-col"
            open={[1]}
            hideLocked
            locked={canManageLibraries(user) ? [] : [0]}
            labels={['Create a New Event', 'Use an Existing Event']}
            onOpen={idx => {
              if (idx === 0) {
                handleSelectEvtTemplate(undefined);
              }
              setIsNewTemplate(idx === 0);
            }}
            headerFn={(label, open, locked) => (
              <>
                {locked ? (
                  <>
                    <FontAwesomeIcon icon="lock" size="lg" fixedWidth />
                    <span className="font-semibold ml-2-5">{label}</span>
                  </>
                ) : (
                  <>
                    <BubbleInput
                      checked={open}
                      type="checkbox"
                      appearance="rounded"
                      className="mr-3"
                      inputClassName="bg-white"
                    />
                    <span className="font-semibold">{label}</span>
                  </>
                )}
              </>
            )}
          >
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Complete the new event details by using the form in the left column. Once finished,
                click the button below to create and select this event.
              </div>
              <BubbleButton
                variant="navy"
                disabled={!formFilter?.title || !formFilter?.defaultDuration}
                onClick={handleCreateEventTemplate}
              >
                Create & Select New Event
              </BubbleButton>
            </div>
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Use the form located in the left column to filter existing events. The list will be
                further filtered as you input information into each field within the form.
              </div>
              <div className="grid grid-cols-1">
                {filteredItems.map(itm => (
                  <div className="relative py-1" key={itm.id}>
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={safeIdCompare(selectedEvtTemplate?._id, itm._template._id)}
                      appearance="rounded"
                      onValue={chk => {
                        handleSelectEvtTemplate(chk ? itm._template : undefined);
                      }}
                    />
                    <TextHighlighted text={itm.title} highlight={formFilter?.title} />
                  </div>
                ))}
              </div>
            </div>
          </BubbleAccordion>
        </div>
      </FormGenericContainer>
    </div>
  );
}
